/**
 * eventTypes.js — the types and colours of a session's events, and how far apart they sit on the
 * vertical timeline. Pure helpers (no React).
 *
 * A game names an event's type and colour (EGDL 4.1's EnableEvent: "Type", "Color" → the portal's
 * eventType / color, "#rrggbb"); both are optional. An event's own colour wins; a type without one
 * gets a series colour picked from its name, so it keeps that colour in every session; an event
 * without a type is "Event", in the gray series colour, as every event was before EGDL 4.1.
 */
import { PALETTE } from '../../charts/palette';

export const UNTYPED = 'Event';
const GRAY = 7; // the series palette's neutral

/** A series colour for a type name: the same name always gets the same colour, never the gray. */
export function defaultTypeColor(type, theme = 'light') {
    const pal = PALETTE[theme] || PALETTE.light;
    if (!type) return pal[GRAY];
    let h = 0x811c9dc5;
    for (let i = 0; i < type.length; i++) {
        h ^= type.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return pal[h % GRAY];
}

/**
 * The types in a session's events, in order of first appearance:
 * [{ key, label, color, count }]. key is the type name ('' for events without one); color is the
 * first colour a game gave an event of that type, else defaultTypeColor.
 */
export function eventTypesOf(events, theme = 'light') {
    const map = new Map();
    (events || []).forEach((e) => {
        const key = e.type || '';
        let t = map.get(key);
        if (!t) {
            t = { key, label: key || UNTYPED, color: null, count: 0 };
            map.set(key, t);
        }
        t.count += 1;
        if (!t.color && e.color) t.color = e.color;
    });
    return [...map.values()].map((t) => ({ ...t, color: t.color || defaultTypeColor(t.key, theme) }));
}

/** The legend's most chips: the most frequent types, then one "Other" for the rest. */
export const LEGEND_MAX = 16;
export const OTHER_KEY = '\u0000other';

/**
 * The legend's chips: [{ key, keys, label, color, count }]. Up to LEGEND_MAX types, the most frequent, in
 * order of first appearance; any more are folded into one "Other" chip whose keys are all of theirs, so a
 * game that names every event differently still gets a legend that fits.
 */
export function legendOf(types, max = LEGEND_MAX, theme = 'light') {
    const list = types || [];
    if (list.length <= max) return list.map((t) => ({ ...t, keys: [t.key] }));
    const kept = new Set([...list].sort((a, b) => b.count - a.count).slice(0, max - 1).map((t) => t.key));
    const rest = list.filter((t) => !kept.has(t.key));
    return [
        ...list.filter((t) => kept.has(t.key)).map((t) => ({ ...t, keys: [t.key] })),
        { key: OTHER_KEY, keys: rest.map((t) => t.key), label: 'Other', color: defaultTypeColor('', theme), count: rest.reduce((n, t) => n + t.count, 0) },
    ];
}

/** Each event with its type's label and the colour it is drawn in (its own, else its type's). */
export function withTypeColors(events, types) {
    const byKey = new Map((types || []).map((t) => [t.key, t]));
    return (events || []).map((e) => {
        const t = byKey.get(e.type || '');
        return { ...e, typeKey: e.type || '', typeLabel: t ? t.label : (e.type || UNTYPED), color: e.color || (t ? t.color : defaultTypeColor('')) };
    });
}

/* ── the timeline's spacing ──────────────────────────────────────────── */

export const PX_PER_SECOND = 10; // a gap is drawn this tall per second ...
export const LONG_GAP = 6; // ... up to this many seconds; a longer one is a break of BREAK_PX
export const BREAK_PX = 64;
export const LABEL_MIN_PX = 18; // the gap's duration is written only where it fits

/** "+4.7 s", "+1:05" */
export function fmtGap(sec) {
    if (sec < 60) return `+${sec.toFixed(1)} s`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec - m * 60);
    return `+${m}:${String(s).padStart(2, '0')}`;
}

/**
 * The space above each event: [{ sec, px, long, label }] (the first event has none: px 0). Events at
 * the same moment touch; a long gap is drawn as a break with its duration always written.
 */
export function timelineGaps(events) {
    return (events || []).map((e, i) => {
        if (i === 0) return { sec: 0, px: 0, long: false, label: '' };
        const sec = Math.max(0, e.t - events[i - 1].t);
        const long = sec > LONG_GAP;
        const px = long ? BREAK_PX : Math.round(sec * PX_PER_SECOND);
        return { sec, px, long, label: long || px >= LABEL_MIN_PX ? fmtGap(sec) : '' };
    });
}
